import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { colorSystem, Flexbox, PrimaryButton, StatusIcon, Text, Tooltip } from '@luminovo/design-system';
import { formatDecimal } from '@luminovo/frontend-utils';
import { Box, Fade, Paper } from '@mui/material';
import { XIcon } from 'lucide-react';
import React, { type JSX, useCallback } from 'react';

export type FloatingSelectionBarProps = {
    open: boolean;
    selectedCount: number;
    /** Selected items that filters hide from the table. */
    hiddenSelectedCount?: number;
    onClearSelection?: () => void;
    onOpenActions?: () => void;
    /** Hides the Actions button when false. */
    hasCommands?: boolean;
    /** Rendered before the Actions button. */
    primaryActionSlot?: React.ReactNode;
    actionsLabel?: string;
    zIndex?: number;
    showShortcutHint?: boolean;
    /** Replaces "{selectedCount} selected", e.g. for a bar that acts on the visible rows, not a selection. */
    countLabel?: React.ReactNode;
};

function trackEvent(_name: string, _data?: Record<string, string | number | boolean>) {
    // Analytics tracking disabled in library code
}

/** A bar floating at the bottom of the table while items are selected. */
export function FloatingSelectionBar({
    open,
    selectedCount,
    hiddenSelectedCount = 0,
    onClearSelection,
    onOpenActions,
    hasCommands = true,
    primaryActionSlot,
    actionsLabel,
    zIndex = 10,
    showShortcutHint = true,
    countLabel,
}: FloatingSelectionBarProps): JSX.Element {
    const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
    const shortcutKey = isMac ? '⌘K' : 'Ctrl+K';
    const hasHiddenSelection = hiddenSelectedCount > 0;

    const handleClearSelection = useCallback(() => {
        trackEvent('selection_cleared', { selected_count: selectedCount });
        onClearSelection?.();
    }, [onClearSelection, selectedCount]);

    const handleOpenActions = useCallback(() => {
        trackEvent('actions_opened', { selected_count: selectedCount });
        onOpenActions?.();
    }, [onOpenActions, selectedCount]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClearSelection();
            }
        },
        [handleClearSelection],
    );

    React.useEffect(() => {
        if (open) {
            trackEvent('shown', { selected_count: selectedCount, hidden_count: hiddenSelectedCount });
        }
    }, [open, selectedCount, hiddenSelectedCount]);

    return (
        <Box
            data-table-command-palette-active={open && hasCommands ? 'true' : undefined}
            sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: 'none',
                zIndex,
            }}
        >
            <Fade in={open} timeout={200}>
                <Paper
                    elevation={8}
                    sx={{
                        pointerEvents: 'auto',
                        borderRadius: '12px',
                        backgroundColor: 'white',
                        border: `1px solid ${colorSystem.neutral[3]}`,
                        boxShadow: `0 4px 16px rgba(0, 0, 0, 0.12), 0 0 0 1px ${colorSystem.neutral[2]}`,
                        padding: '8px 12px',
                        marginBottom: '4px',
                    }}
                >
                    <Flexbox
                        gap="12px"
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        {hasHiddenSelection && (
                            <Tooltip
                                title={t`${formatDecimal(hiddenSelectedCount)} selected item(s) are hidden due to active filters`}
                                variant="white"
                            >
                                <Box component="span" role="img" sx={{ display: 'flex', alignItems: 'center' }}>
                                    <StatusIcon
                                        status="warning"
                                        size={16}
                                        style={{ color: colorSystem.yellow[7], cursor: 'help' }}
                                    />
                                </Box>
                            </Tooltip>
                        )}

                        <Tooltip title={onClearSelection ? t`Click to clear selection` : ''} variant="white">
                            <Flexbox
                                gap="6px"
                                onClick={handleClearSelection}
                                onKeyDown={onClearSelection ? handleKeyDown : undefined}
                                role={onClearSelection ? 'button' : undefined}
                                tabIndex={onClearSelection ? 0 : undefined}
                                aria-label={onClearSelection ? t`Clear selection` : undefined}
                                sx={{
                                    alignItems: 'center',
                                    height: '32px',
                                    backgroundColor: colorSystem.neutral[2],
                                    borderRadius: '6px',
                                    padding: '0 10px',
                                    cursor: onClearSelection ? 'pointer' : 'default',
                                    transition: 'background-color 0.15s ease',

                                    '&:hover': onClearSelection
                                        ? {
                                              backgroundColor: colorSystem.neutral[3],
                                          }
                                        : {},

                                    '&:focus-visible': {
                                        outline: `2px solid ${colorSystem.primary[5]}`,
                                        outlineOffset: '2px',
                                    },
                                }}
                            >
                                <Text variant="body-semibold" color={colorSystem.neutral[8]}>
                                    {countLabel ?? <Trans>{formatDecimal(selectedCount)} selected</Trans>}
                                </Text>
                                {onClearSelection && (
                                    <XIcon
                                        size={14}
                                        color={colorSystem.neutral[6]}
                                        style={{ transition: 'color 0.15s ease' }}
                                    />
                                )}
                            </Flexbox>
                        </Tooltip>

                        {(primaryActionSlot || (hasCommands && onOpenActions)) && (
                            <Box
                                sx={{
                                    width: '1px',
                                    height: '40px',
                                    backgroundColor: colorSystem.neutral[4],
                                }}
                            />
                        )}

                        {primaryActionSlot}

                        {hasCommands && onOpenActions && (
                            <Tooltip title={t`Open command palette`} variant="white">
                                <PrimaryButton size="medium" onClick={handleOpenActions}>
                                    <Flexbox
                                        gap="8px"
                                        sx={{
                                            alignItems: 'center',
                                        }}
                                    >
                                        <span>{actionsLabel ?? <Trans>Actions</Trans>}</span>
                                        {showShortcutHint && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    fontSize: '11px',
                                                    opacity: 0.7,
                                                    fontWeight: 400,
                                                }}
                                            >
                                                {shortcutKey}
                                            </Box>
                                        )}
                                    </Flexbox>
                                </PrimaryButton>
                            </Tooltip>
                        )}
                    </Flexbox>
                </Paper>
            </Fade>
        </Box>
    );
}
